import React, { useState, useEffect } from 'react';
import { getActiveQuizzes, getQuizQuestions,checkQuizAttempt, submitQuizAttempt } from '../../api';
import { toast } from 'react-toastify';

const StudentQuizzes = () => {
  const [quizzes, setQuizzes] = useState([]);
  const [activeQuiz, setActiveQuiz] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [result, setResult] = useState(null);
  const [attemptedQuizzes, setAttemptedQuizzes] = useState({});

  useEffect(() => { loadQuizzes(); }, []);

  const loadQuizzes = async () => {
    try {
      const res = await getActiveQuizzes();
      setQuizzes(res.data);

      const attemptStatus = {};
      await Promise.all(res.data.map(async (q) => {
        try {
          const check = await checkQuizAttempt(q.id);
          attemptStatus[q.id] = check.data;
        } catch {
          attemptStatus[q.id] = { attempted: false };
        }
      }));
      setAttemptedQuizzes(attemptStatus);
    } catch (err) {
      toast.error('Error loading quizzes');
    }
  };

  const startQuiz = async (quizId) => {
    try {
      const res = await getQuizQuestions(quizId);
      setQuestions(res.data);
      setActiveQuiz(quizId);
      setAnswers({});
      setSubmitted(false);
      setResult(null);
    } catch (err) {
      toast.error('Error loading quiz');
    }
  };

  const handleAnswer = (questionId, answer) => {
    setAnswers(prev => ({ ...prev, [questionId]: answer }));
  };

  const handleSubmit = async () => {
    if (Object.keys(answers).length < questions.length) {
      return toast.error('Please answer all questions!');
    }
    try {
      const res = await submitQuizAttempt({ quiz_id: activeQuiz, answers });
      setResult(res.data);
      setSubmitted(true);
      toast.success('Quiz submitted!');
    } catch (err) {
      if (err.response?.status === 400) {
        toast.error(err.response.data.message);
        setActiveQuiz(null);
      } else {
        toast.error('Error submitting quiz');
      }
    }
  };

  if (submitted && result) {
    return (
      <div style={styles.resultContainer}>
        <div style={styles.resultCard}>
          <h2 style={styles.resultIcon}>🎉</h2>
          <h3 style={styles.resultTitle}>Quiz Completed!</h3>
          <h2 style={styles.resultMarks}>{result.obtained_marks} / {result.total_marks} Marks</h2>
          <p style={styles.resultText}>You have successfully submitted the quiz!</p>
          <button style={styles.backBtn} onClick={() => {
            setActiveQuiz(null);
            setSubmitted(false);
            setResult(null);
            loadQuizzes();
          }}>Back to Quizzes</button>
        </div>
      </div>
    );
  }

  if (activeQuiz) {
    return (
      <div>
        <div style={styles.quizHeader}>
          <h3 style={styles.title}>📝 Quiz In Progress</h3>
          <span style={styles.progress}>
            {Object.keys(answers).length}/{questions.length} Answered
          </span>
        </div>

        {questions.map((q, index) => (
          <div key={q.id} style={styles.questionCard}>
            <h4 style={styles.questionText}>Q{index + 1}. {q.question}</h4>
            <p style={styles.marksText}>Marks: {q.marks}</p>
            <div style={styles.optionsGrid}>
              {['a', 'b', 'c', 'd'].map(opt => (
                q[`option_${opt}`] && (
                  <button key={opt}
                    style={{
                      ...styles.optionBtn,
                      ...(answers[q.id] === opt ? styles.optionSelected : {})
                    }}
                    onClick={() => handleAnswer(q.id, opt)}>
                    <span style={styles.optionLabel}>{opt.toUpperCase()}.</span>
                    {q[`option_${opt}`]}
                  </button>
                )
              ))}
            </div>
          </div>
        ))}

        <button style={styles.submitBtn} onClick={handleSubmit}>
          ✅ Submit Quiz
        </button>
      </div>
    );
  }

  return (
    <div>
      <h3 style={styles.title}>❓ Active Quizzes</h3>

      {quizzes.length === 0 && (
        <div style={styles.empty}>No active quizzes right now!</div>
      )}

      <div style={styles.quizGrid}>
        {quizzes.map(q => (
          <div key={q.id} style={styles.quizCard}>
            <h4 style={styles.quizTitle}>{q.title}</h4>
            <p style={styles.quizSubject}>📚 {q.subject_name}</p>
            <p style={styles.quizDuration}>⏱️ {q.duration_minutes} minutes</p>

            {attemptedQuizzes[q.id]?.attempted ? (
              <div style={styles.attemptedBox}>
                ✅ Attempt ho chuka hai
                <br />
                <span style={styles.attemptedMarks}>
                  Marks: {attemptedQuizzes[q.id].result?.obtained_marks} / {attemptedQuizzes[q.id].result?.total_marks}
                </span>
              </div>
            ) : (
              <button style={styles.startBtn} onClick={() => startQuiz(q.id)}>
                Start Quiz →
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const styles = {
  title: { color: '#1a3a5c', marginBottom: '20px' },
  empty: { textAlign: 'center', color: '#999', padding: '40px', background: 'white', borderRadius: '12px' },
  quizGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '15px' },
  quizCard: { background: 'white', borderRadius: '12px', padding: '20px', boxShadow: '0 2px 10px rgba(0,0,0,0.08)', borderTop: '4px solid #0d6efd' },
  quizTitle: { color: '#1a3a5c', margin: '0 0 8px', fontSize: '16px' },
  quizSubject: { color: '#666', fontSize: '13px', margin: '0 0 5px' },
  quizDuration: { color: '#666', fontSize: '13px', margin: '0 0 15px' },
  startBtn: { padding: '10px 20px', background: '#0d6efd', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', width: '100%' },
  attemptedBox: { padding: '10px 15px', background: '#d4edda', borderRadius: '8px', color: '#155724', fontSize: '13px', textAlign: 'center', fontWeight: '600' },
  attemptedMarks: { color: '#155724', fontSize: '13px', fontWeight: '400' },
  quizHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' },
  progress: { background: '#e8f0fe', color: '#1a3a5c', padding: '8px 15px', borderRadius: '20px', fontSize: '14px', fontWeight: '600' },
  questionCard: { background: 'white', borderRadius: '12px', padding: '20px', marginBottom: '15px', boxShadow: '0 2px 10px rgba(0,0,0,0.08)' },
  questionText: { color: '#1a3a5c', margin: '0 0 5px', fontSize: '15px' },
  marksText: { color: '#666', fontSize: '12px', margin: '0 0 15px' },
  optionsGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' },
  optionBtn: { padding: '12px 15px', background: '#f8f9fa', border: '2px solid #e0e0e0', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', textAlign: 'left', display: 'flex', gap: '8px', alignItems: 'center' },
  optionSelected: { background: '#e8f0fe', border: '2px solid #0d6efd', color: '#0d6efd' },
  optionLabel: { fontWeight: 'bold', minWidth: '20px' },
  submitBtn: { padding: '14px 30px', background: '#28a745', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '15px', fontWeight: '600', marginTop: '10px' },
  resultContainer: { display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' },
  resultCard: { background: 'white', borderRadius: '16px', padding: '40px', textAlign: 'center', boxShadow: '0 10px 40px rgba(0,0,0,0.1)', maxWidth: '400px', width: '100%' },
  resultIcon: { fontSize: '60px', margin: '0 0 10px' },
  resultTitle: { color: '#1a3a5c', margin: '0 0 20px', fontSize: '22px' },
  resultMarks: { color: '#28a745', fontSize: '40px', margin: '0 0 10px' },
  resultText: { color: '#666', fontSize: '14px', margin: '0 0 20px' },
  backBtn: { padding: '12px 25px', background: '#0d6efd', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px' },
};

export default StudentQuizzes;